import { serializeQuery } from "@xtribe/shared/lib/text-transformations";
import { Content } from "@prisma/client";
import axios, { AxiosError } from "axios";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import {
  IFetchUserEvents,
  EventContentData,
  FetchEventsContentProps,
} from "./types";

async function fetchEvents({
  widget,
  ...data
}: FetchEventsContentProps & { widget?: boolean }): Promise<
  EventContentData[]
> {
  const query = serializeQuery({
    ...data,
  });
  try {
    if (widget) {
      const res = await axios.get(
        `/api/v1/widgets/calendar/list${query ? "?" + query : ""}`
      );
      return res.data;
    }
    const res = await axios.get(
      `/api/v1/content/events/list${query ? "?" + query : ""}`
    );
    return res.data;
  } catch (error) {
    if (error?.response?.data?.message) {
      throw new Error(error?.response?.data?.message);
    } else throw error;
  }
}

function useEvents<TData = EventContentData[]>(
  data: FetchEventsContentProps & { widget?: boolean },
  options?: UseQueryOptions<EventContentData[], AxiosError, TData>
) {
  return useQuery(
    [
      "events",
      {
        ...data,
      },
    ],
    () =>
      fetchEvents({
        ...data,
      }),
    options
  );
}

async function fetchUserEvents({
  userId,
  ...data
}: IFetchUserEvents): Promise<EventContentData[]> {
  const query = serializeQuery({
    ...data,
  });
  try {
    const res = await axios.get(
      `/api/v1/users/${userId}/events/list${query ? "?" + query : ""}`
    );
    return res.data;
  } catch (error) {
    if (error?.response?.data?.message) {
      throw new Error(error?.response?.data?.message);
    } else throw error;
  }
}
function useUserEvents<TData = EventContentData[]>(
  data: IFetchUserEvents,
  options?: UseQueryOptions<EventContentData[], AxiosError, TData>
) {
  return useQuery(
    [
      "userEvents",
      {
        ...data,
      },
    ],
    () =>
      fetchUserEvents({
        ...data,
      }),
    options
  );
}

function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation(
    (values: any) =>
      axios
        .post(`/api/v1/content/events/create`, values)
        .then((res) => res.data)
        .catch((error) => {
          if (error?.response?.data?.message) {
            throw new Error(error?.response?.data?.message);
          } else throw error;
        }),
    {
      onSuccess: (data, values) => queryClient.invalidateQueries("events"),
    }
  );
}

export { useEvents, fetchEvents, useCreateEvent, useUserEvents };
